import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import PlanActions from "../../Redux/transition/plan/plan.reducer";
import PaymentTrackActions from "../../Redux/transition/payment-track/payment-track.reducer";
import DefMembershipActions from "../../Redux/actions/defMembership-actions";
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import CustomDataTable from "../../Components/CustomDataTable";
import { FormatCurrency, CurrencyFormatter, useCurrencyConstants } from '../../Components/CurrencyFormatter';
import FilterDropdown from "../../Components/DateRange";
import { getDateRange, isLifetimePeriod, resolveLifetimeStart } from "../../Components/DateRange";
import { useDateFormatter } from "../../Components/FormatDateTime"
import "../../Styles/pages/product.scss";
import { Button } from "primereact/button";

const PAYMENT_SUMMARY_MOBILE_BREAKPOINT = 1050;

const Plans = () => {

    const { t } = useTranslation("common");
    const { currencyFormat, currencySymbol } = useCurrencyConstants();
    const toast = useRef(null);
    const { formatApiDate, formatDate } = useDateFormatter();

    const periods = [
        { code: "Today", value: t("dashboard.today") },
        { code: "Last week", value: t("dashboard.last_week") },
        { code: "This month", value: t("dashboard.this_month") },
        { code: "Last month", value: t("dashboard.last_month") },
        { code: "Last 3 months", value: t("dashboard.3_months_duration") },
        { code: "Last 6 months", value: t("dashboard.6_months_duration") },
        { code: "Last 12 months", value: t("dashboard.12_months_duration") },
        { code: "Year to date", value: t("dashboard.year_to_date") },
        { code: "Lifetime", value: t("dashboard.lifetime") },
        { code: "Custom date", name: t("dashboard.custom_date") }
    ];

    const [calendarDates, setCalendarDates] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [cleared, setCleared] = useState(false);
    const [duration, setDuration] = useState(t("dashboard.this_month"));
    const [showCustomDate, setShowCustomDate] = useState(false);
    const [first, setFirst] = useState(0);
    const [pageLoading, setPageLoading] = useState(false);
    const rows = 15;

    const [currentDateRange, setCurrentDateRange] = useState({ startDate: null, endDate: null });

    const [pdfRequested, setPdfRequested] = useState(false);
    const [isMobileStack, setIsMobileStack] = useState(
        () => typeof window !== "undefined" && window.innerWidth <= PAYMENT_SUMMARY_MOBILE_BREAKPOINT
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${PAYMENT_SUMMARY_MOBILE_BREAKPOINT}px)`);
        const onBreakpointChange = (event) => setIsMobileStack(event.matches);
        onBreakpointChange(mediaQuery);
        mediaQuery.addEventListener("change", onBreakpointChange);
        return () => mediaQuery.removeEventListener("change", onBreakpointChange);
    }, []);

    // eslint-disable-next-line no-unused-vars
    const [state, setState] = useState({
        planPaymentSummary: []
    })

    //Redux Dispatch Actions
    const dispatch = useDispatch();
    const getPlanPaymentSummary = useCallback((businessId, startDate, endDate) => { dispatch(PlanActions.planPaymentSummaryRequest(businessId, startDate, endDate)) }, [dispatch]);
    const getDefMembership = useCallback(() => { dispatch(DefMembershipActions.defMembershipRequest()); }, [dispatch])
    const getPaymentsSummaryPdf = useCallback((businessId, membershipId, startDate, endDate) => { dispatch(PaymentTrackActions.paymentTracksSummaryPdfRequest(businessId, membershipId, startDate, endDate)) }, [dispatch]);

    //Redux State from Store
    const defMembership = useSelector((state) => state.defMembership.defMembership);
    const { planPaymentSummary, fetchingPlanPaymentSummary } = useSelector((state) => state.plans);
    const {
        paymentTracksSummaryPdf,
        fetchingPaymentTracksSummaryPdf,
        errorFetchingPaymentTracksSummaryPdf
    } = useSelector((state) => state.paymentTracks);

    const businessId = defMembership?.business?.id;
    const planGroupsGraphedAll = useSelector((state) => state.planGroups.planGroupsGraphedAll);
    const planGroupsGraphedActive = useSelector((state) => state.planGroups.planGroupsGraphedActive);
    const lifetimeStart = useMemo(
        () => resolveLifetimeStart({ graphed: planGroupsGraphedAll || planGroupsGraphedActive }),
        [planGroupsGraphedAll, planGroupsGraphedActive]
    );

    useEffect(() => {
        if (!defMembership) {
            getDefMembership();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (planPaymentSummary) {
            setState((state) => ({ ...state, planPaymentSummary: planPaymentSummary.summaries }));
        }
    }, [planPaymentSummary]);

    useEffect(() => {
        if (pdfRequested && paymentTracksSummaryPdf && !fetchingPaymentTracksSummaryPdf) {
            const url = window.URL.createObjectURL(new Blob([paymentTracksSummaryPdf], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `payment_summary_report_${currentDateRange.startDate}_${currentDateRange.endDate}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.current.show({
                severity: 'success',
                summary: t('success'),
                detail: t('plan.pdf_download_success'),
                life: 3000
            });

            setPdfRequested(false);
        }
    }, [paymentTracksSummaryPdf, fetchingPaymentTracksSummaryPdf, pdfRequested, currentDateRange, t]);

    useEffect(() => {
        if (pdfRequested && errorFetchingPaymentTracksSummaryPdf && !fetchingPaymentTracksSummaryPdf) {
            toast.current.show({
                severity: 'error',
                summary: t('error.error'),
                detail: t('plan.pdf_download_error'),
                life: 3000
            });

            setPdfRequested(false);
        }
    }, [errorFetchingPaymentTracksSummaryPdf, fetchingPaymentTracksSummaryPdf, pdfRequested, t]);

    const getDates = (range) => {
        const { startDate, endDate } = getDateRange(range, { returnRange: true, lifetimeStart });
        if (!startDate || !endDate) return null;

        return {
            startDate: formatApiDate(startDate),
            endDate: formatApiDate(endDate)
        };
    };

    const fetchPlanSummaryReport = (range) => {
        const dates = getDates(range);
        if (!dates?.startDate || !dates?.endDate) return;
        setCurrentDateRange(dates);
        getPlanPaymentSummary(businessId, dates.startDate, dates.endDate);
    }

    const onPageChange = (event) => {
        if (event.first !== first) {
            setPageLoading(true);
            setFirst(event.first);

            setTimeout(() => {
                setPageLoading(false);
            }, 200);
        }
    };

    useEffect(() => {
        fetchPlanSummaryReport(duration);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDurationChange = (e) => {
        setDuration(e.value);
        const periodValue = e.value;
        if (periodValue.code !== 'Custom date') {
            setCalendarDates(null);
            setShowCustomDate(false);
            if (!isLifetimePeriod(periodValue)) {
                fetchPlanSummaryReport(e.value);
            }
        } else setShowCustomDate(true);
    }

    useEffect(() => {
        if (!isLifetimePeriod(duration) || !lifetimeStart) return;
        fetchPlanSummaryReport(duration);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duration, lifetimeStart]);

    const dateRangeForTotals = getDateRange(duration, { returnRange: true, lifetimeStart })
    const start = formatDate(calendarDates ? calendarDates[0] : dateRangeForTotals?.startDate);
    const end = formatDate(calendarDates ? calendarDates[1] : dateRangeForTotals?.endDate)

    const handleCalendarSelect = (e) => {
        const range = e.value;
        setCalendarDates(range);
        if (range && range[1] !== null && range.length === 2) {
            const [startDate, endDate] = range;
            const formattedStart = formatApiDate(startDate);
            const formattedEnd = formatApiDate(endDate);
            setCurrentDateRange({ startDate: formattedStart, endDate: formattedEnd });
            getPlanPaymentSummary(businessId, formattedStart, formattedEnd);
        }
    };

    const handleExportPdf = () => {
        if (!currentDateRange.startDate || !currentDateRange.endDate) {
            toast.current.show({
                severity: 'warn',
                summary: t('common.warning'),
                detail: t('plan.select_date_range'),
                life: 3000
            });
            return;
        }
        setPdfRequested(true);
        getPaymentsSummaryPdf(businessId, defMembership.id, currentDateRange.startDate, currentDateRange.endDate);
    };

    const planBodyTemplate = (rowData) => {
        return <div className="flex text-align-left gap-2" id="plan-body-div">
            <span className="plan_body">{rowData.planName}</span>
            <span className="group_body">{rowData.groupName}</span>
        </div>
    };

    const numberPaymentsTemplate = (rowData) => {
        return <div className="flex text-align-left">
            <span className="incidence">{rowData.incidence}</span>
        </div >
    };

    const contributorsNumberTemplate = (rowData) => {
        return <div className="flex text-align-left">
            <span className="incidence">{rowData.contributors}</span>
        </div >
    };

    const collectedBodyTemplate = (rowData) => {
        return <div className="flex text-align-left">
            <FormatCurrency
                amount={rowData.collected}
                currencySymbol={currencySymbol}
                currencyFormat={currencyFormat}
                className='planCollected'
            />
        </div>
    };

    const serviceFeesBodyTemplate = (rowData) => {
        return <div className="flex text-align-left">
            <FormatCurrency
                amount={rowData.fees}
                currencySymbol={currencySymbol}
                currencyFormat={currencyFormat}
                className='feePayment'
            />
        </div>
    };

    const revenueBodyTemplate = (rowData) => {
        return <div className="flex text-align-left">
            <FormatCurrency
                amount={rowData.revenue}
                currencySymbol={currencySymbol}
                currencyFormat={currencyFormat}
                className='revenue'
            />
        </div>
    };

    const paymentSummaryMobileTemplate = (rowData) => (
        <div className="payment-summary-mobile-summary">
            <div className="payment-summary-mobile-section">{planBodyTemplate(rowData)}</div>
            <div className="payment-summary-mobile-section">{numberPaymentsTemplate(rowData)}</div>
            <div className="payment-summary-mobile-section">{contributorsNumberTemplate(rowData)}</div>
            <div className="payment-summary-mobile-section">{collectedBodyTemplate(rowData)}</div>
            <div className="payment-summary-mobile-section">{serviceFeesBodyTemplate(rowData)}</div>
            <div className="payment-summary-mobile-section payment-summary-mobile-section-last">{revenueBodyTemplate(rowData)}</div>
        </div>
    );

    const desktopColumns = [
        {
            field: "plans",
            header: t("plan.title"),
            body: planBodyTemplate,
            alignHeader: "left"
        },
        {
            field: 'numberPayments',
            header: t("plan.num_payments"),
            body: numberPaymentsTemplate,
            alignHeader: "left"
        },
        {
            field: 'contributors',
            header: t("plan.contributors_num"),
            body: contributorsNumberTemplate,
            alignHeader: "left"
        },
        {
            field: 'collectedPayments',
            header: t("product.collected"),
            body: collectedBodyTemplate,
            alignHeader: "left"
        },
        {
            field: 'serviceFee',
            header: t("product.service_fee"),
            body: serviceFeesBodyTemplate,
            alignHeader: "left"
        },
        {
            field: 'planRevenue',
            header: t("common.revenue"),
            body: revenueBodyTemplate,
            alignHeader: "left"
        }
    ];

    const mobileSummaryColumn = {
        field: "mobileSummary",
        header: t("plan.title"),
        body: paymentSummaryMobileTemplate,
        className: "payment-summary-mobile-summary-column",
        alignHeader: "left",
    };

    const columns = isMobileStack ? [mobileSummaryColumn] : desktopColumns;

    const loading = fetchingPlanPaymentSummary;

    return (
        <div className="products-screen payment-summary-screen">
            <Toast ref={toast} />

            <Card id="payment-action-bar" className="plan_action_card payment-summary-card">
                <div className="payment-summary-filters">
                    <FilterDropdown
                        value={duration}
                        options={periods}
                        onChange={onDurationChange}
                        optionLabel="code"
                        autoSetActiveId={true}
                        id="period"
                        showHint={true}
                        hintText={t("plan.reporting_period_hint")}
                        containerClass="payment-summary-period-filter filters"
                    />

                    {showCustomDate && (
                        <div className="payment-summary-calendar plan-filter-calendar">
                            <Calendar
                                className="p-datepicker-header report-calender datepicker"
                                id='report-calender'
                                value={calendarDates}
                                showIcon
                                showButtonBar
                                onClearButtonClick={() => { setCleared(true) }}
                                dateFormat="dd M yy"
                                maxDate={new Date()}
                                onChange={handleCalendarSelect}
                                selectionMode="range"
                                readOnlyInput
                                placeholder={t("report.choose_date")}
                            />
                            <p className="reporting_period_hint">{t("plan.reporting_period_hint")}</p>
                        </div>
                    )}
                </div>

                <hr className="hr_payment_summary" />

                <div className="action_bar_totals payment-summary-totals">
                    {(start || end) && <p className="total_hint_style">{t("plan.total_hint", { startDate: start, endDate: end })}</p>}

                    <div className="payment-summary-totals-row">
                        <div className="payment-summary-totals-grid">
                            <div className="payment-summary-total-item">
                                <span className="bold_number_text">{t("plan.num_payments")}</span><br />
                                <span className="bold_number_value">{planPaymentSummary?.incidence}</span>
                            </div>
                            <div className="payment-summary-total-item">
                                <span className="bold_number_text">{t("plan.contributors_num")}</span><br />
                                <span className="bold_number_value">{planPaymentSummary?.contributors}</span>
                            </div>
                            <div className="payment-summary-total-item">
                                <span className="green-success-txts">{t("product.collected")}</span><br />
                                <CurrencyFormatter
                                    amount={planPaymentSummary?.collected}
                                    currencySymbol={currencySymbol}
                                    currencyFormat={currencyFormat}
                                    valueClass="amt-fig p-1"
                                    symbolClass="count-curren p-0"
                                />
                            </div>
                            <div className="payment-summary-total-item">
                                <span className="red-cancelled-txts">{t("product.service_fee")}</span><br />
                                <CurrencyFormatter
                                    amount={planPaymentSummary?.fees}
                                    currencySymbol={currencySymbol}
                                    currencyFormat={currencyFormat}
                                    valueClass="amt-fig p-1"
                                    symbolClass="count-curren p-0"
                                />
                            </div>
                            <div className="payment-summary-total-item">
                                <span className="blue-revenue-txt">{t("common.revenue")}</span><br />
                                <CurrencyFormatter
                                    amount={planPaymentSummary?.revenue}
                                    currencySymbol={currencySymbol}
                                    currencyFormat={currencyFormat}
                                    valueClass="amt-fig p-1"
                                    symbolClass="count-curren p-0"
                                />
                            </div>
                        </div>
                        <div className="payment-summary-export plan-filter-export">
                            <Button
                                className="export-pdf-btn pr-2"
                                onClick={handleExportPdf}
                                loading={fetchingPaymentTracksSummaryPdf}
                                icon={!fetchingPaymentTracksSummaryPdf ? "pi pi-download" : null}
                                iconPos="left"
                            >
                                {t('export_pdf')}
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>

            <CustomDataTable
                key={`payment-summary-table-${isMobileStack}`}
                value={planPaymentSummary?.summaries}
                columns={columns}
                paginator
                first={first}
                rows={rows}
                loading={pageLoading || loading}
                onPage={onPageChange}
                className="customed-datatable payment-summary-data-table"
                responsiveLayout="stack"
                scrollable={!isMobileStack}
            />
        </div>
    )
};
export default Plans;